import { type RowQuantityType } from '../lscript/row_types';
import {
  areaUnits,
  calendarUnits,
  carbonUnits,
  countUnits,
  currencyUnits,
  energyEconomyUnits,
  energyUnits,
  forceUnits,
  fuelEconomyUnits,
  informationUnits,
  lengthUnits,
  massUnits,
  powerUnits,
  storageUnits,
  temperatureUnits,
  textileUnits,
  timeUnits,
  transportUnits,
  volumeUnits,
} from './definitions';
import { parseUnitString } from './parse';
import { unitToString } from './registry';
import { Unit, UnitGroup } from './types';

/**
 * Maps from unit group to a non-exhaustive list of units in that group.
 *
 * @deprecated Use `isUnitCommensurable` instead of relying on unit groups
 * to check commensurability.
 */
export const UNIT_GROUP_TO_UNITS = new Map<UnitGroup, Array<Unit>>([
  ['area', areaUnits],
  ['length', lengthUnits],
  ['mass', massUnits],
  ['volume', volumeUnits],
  ['time', timeUnits],
  ['calendar', calendarUnits],
  ['fuelEconomy', fuelEconomyUnits],
  ['energyEconomy', energyEconomyUnits],
  ['temperature', temperatureUnits],
  ['textile', textileUnits],
  ['energy', energyUnits],
  ['power', powerUnits],
  ['count', countUnits],
  ['currency', currencyUnits],
  ['information', informationUnits],
  ['force', forceUnits],
  ['carbon', carbonUnits],
  ['storage', storageUnits],
  ['transport', transportUnits],
]);

/**
 * Get a non-exhaustive list of units in a unit group.
 *
 * @deprecated Use `isUnitCommensurable` instead of relying on unit groups
 * to check commensurability.
 */
export function getUnitsFromUnitGroup(unitGroup: UnitGroup): Array<Unit> {
  return UNIT_GROUP_TO_UNITS.get(unitGroup) ?? [parseUnitString('count')];
}

/**
 * Get the unit group for a unit, or undefined if no named unit group matches.
 *
 * @deprecated Use `isUnitCommensurable` instead of relying on unit groups
 * to check commensurability.
 */
export function getUnitGroupFromUnit(
  unit: Unit | undefined
): UnitGroup | undefined {
  if (unit) {
    for (const [unitGroup, unitList] of UNIT_GROUP_TO_UNITS) {
      if (unitList.includes(unit)) {
        return unitGroup;
      }
    }
  }
}

/**
 * Maps from unit group to the default unit string for that unit group.
 *
 * @deprecated "Default" units can be use-case specific and should be specified
 * at a layer above the unit system.
 */
export const UNIT_GROUP_TO_DEFAULT_UNIT_STRING: Record<UnitGroup, string> = {
  area: 'square_foot',
  length: 'kilometer',
  mass: 'kilogram',
  volume: 'liter',
  time: 'hour',
  calendar: 'year',
  fuelEconomy: 'l/100km',
  energyEconomy: 'kWh/km',
  temperature: 'celsius',
  textile: 'tex',
  energy: 'kilowatt-hour',
  power: 'kilowatt',
  count: 'count',
  currency: 'USD',
  information: 'gigabyte',
  force: 'newton',
  carbon: 'kgco2e',
  storage: 'gigabyte-hour',
  transport: 'tonne-kilometer',
};

export function makeQuantityType(
  unitGroup: UnitGroup,
  unitCol: string
): RowQuantityType {
  const units = getUnitsFromUnitGroup(unitGroup);
  return {
    ty: 'quantity',
    units,
    encodedValuesToUnits: new Map(
      units.map((unit) => [unitToString(unit), unit])
    ),
    column: unitCol,
  };
}
